import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2MigrationRow } from "./sstv2MigrationTypes"

/**
 * Per-locker lifecycle stepper (Item 5, DEV-193). Shows where a locker is in the
 * V1→V2 migration and which `migration:*` event is expected next. Two shapes:
 *  - forward: Migrated → Operational (cutover) → Completed (history)
 *  - rollback branch: Rollback started → Rolled back
 * The stepper is a legibility aid on top of the raw timeline (kept below it in
 * the drawer), turning "a pile of event strings" into "you are HERE, waiting for
 * X". As a passive observer we can only say what's expected, not force it.
 */

type StepDef = {
  labelKey: string
  /**
   * One-line plain-English gloss for this milestone, shown on the step itself.
   *
   * These are PARTNER milestones, and the most valuable thing each gloss does is say
   * what the milestone does NOT mean — "Completed (history)" in particular announces
   * that the PARTNER finished sending, which since D37 is a long way from our own
   * work being done.
   */
  glossKey: string
  /** the event that MOVES the locker INTO this step (what we're waiting for). */
  expectedEvent: string
}

const FORWARD_STEPS: StepDef[] = [
  {
    labelKey: "migrationStepMigrated",
    glossKey: "migrationStepMigratedGloss",
    expectedEvent: "migration:locker:migrated",
  },
  {
    labelKey: "migrationStepOperational",
    glossKey: "migrationStepOperationalGloss",
    expectedEvent: "migration:locker:operational",
  },
  {
    labelKey: "migrationStepCompleted",
    glossKey: "migrationStepCompletedGloss",
    expectedEvent: "migration:reservations:history:completed",
  },
]

const ROLLBACK_STEPS: StepDef[] = [
  {
    labelKey: "migrationStepRollbackStarted",
    glossKey: "migrationStepRollbackStartedGloss",
    expectedEvent: "migration:rollback:started",
  },
  {
    labelKey: "migrationStepRolledBack",
    glossKey: "migrationStepRolledBackGloss",
    expectedEvent: "migration:rollback:done",
  },
]

/** activeStep = number of milestones already reached (MUI completes idx < it). */
const forwardActiveStep = (phase: Sstv2MigrationRow["phase"]): number => {
  switch (phase) {
    case "ACTIVE_MIGRATED":
      return 1
    case "OPERATIONAL":
      return 2
    case "COMPLETED":
      return 3
    default:
      return 0
  }
}

const rollbackActiveStep = (phase: Sstv2MigrationRow["phase"]): number =>
  phase === "ROLLED_BACK" ? 2 : 1

export const Sstv2LifecycleStepper: FC<{ row: Sstv2MigrationRow }> = ({
  row,
}) => {
  const { t } = useTranslation()

  const steps = row.isRollback ? ROLLBACK_STEPS : FORWARD_STEPS
  const activeStep = row.isRollback
    ? rollbackActiveStep(row.phase)
    : forwardActiveStep(row.phase)

  // The step the locker is currently WAITING to reach (undefined when done).
  const pendingStep = activeStep < steps.length ? steps[activeStep] : undefined

  return (
    <Box>
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        sx={{
          "& .MuiStepIcon-root.Mui-active": { color: "primary.main" },
          "& .MuiStepIcon-root.Mui-completed": { color: "success.main" },
          ...(row.isRollback && {
            "& .MuiStepIcon-root.Mui-active": { color: "error.main" },
            "& .MuiStepIcon-root.Mui-completed": { color: "error.main" },
          }),
        }}
      >
        {steps.map((s) => (
          <Step key={s.labelKey}>
            <StepLabel
              optional={
                <Typography variant="caption" color="text.secondary">
                  {t(s.glossKey)}
                </Typography>
              }
            >
              {t(s.labelKey)}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {pendingStep ? (
        <Typography variant="caption" color="text.secondary">
          {t("migrationExpectedNext")}:{" "}
          <Box component="span" fontFamily="monospace">
            {pendingStep.expectedEvent}
          </Box>
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {row.isRollback
            ? t("migrationRollbackComplete")
            : t("migrationAllStepsComplete")}
        </Typography>
      )}
    </Box>
  )
}

export default Sstv2LifecycleStepper
